import { IconClipboardList, IconInfoCircle, IconTrash } from '@tabler/icons-react';
import { useAuth } from '../../../../auth/context/AuthProvider';
import React, { useCallback, useEffect, useState } from 'react';
import { OverlayTrigger, Tooltip, Button } from 'react-bootstrap';
import useShowBooking from '../../../../services/booking/hooks/useShowBooking';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import CurrencyInputRef from '../../../../shared/components/form/CurrencyInputRef';
import DateInput from '../../../../shared/components/form/DateInput';
import Loader from '../../../../shared/components/Loader';
import { useCreateBookingExtra } from '../../../../services/booking-extra/hooks/useCreateBookingExtra';
import { CreateBookingExtraFormData, CreateBookingExtraSchema } from '../../../../services/booking-extra/schema/CreateBookingExtraSchema';
import useIndexBookingExtra from '../../../../services/booking-extra/hooks/useIndexBookingExtra';
import { useDeleteBookingExtra } from '../../../../services/booking-extra/hooks/useDeleteBookingExtra';
import SelectInventoryModal from '../../../../shared/components/SelectInventoryModal';
import ExtraDescription from '../../../../shared/components/ExtraDescription';
import { isPosExtra, isCancelledExtra, POS_LOCK_MESSAGE } from '../../../../shared/utils/posExtra';

const InfoTooltip = ({ message }: { message: string }) => (
    <OverlayTrigger
        placement="top"
        overlay={<Tooltip>{message}</Tooltip>}
    >
        <span style={{ cursor: 'pointer' }} className="text-muted ms-1">
            <IconInfoCircle size={16} />
        </span>
    </OverlayTrigger>
);

type Props = {
    reservationId?: string;
    onClose?: () => void;
}

const ExtraSection: React.FC<Props> = ({ reservationId }) => {
    const { hasRole, hasPermission } = useAuth();
    const { data: reservationData, isFetching: isReservationFetching, isSuccess: isReservationSuccess } = useShowBooking({
        bookingId: reservationId || ''
    });

    const { data: extrasData, isFetching: isExtrasFetching, isSuccess: isExtrasSuccess } = useIndexBookingExtra({
        filters: {
            booking_room_id: reservationId || '',
        },
    });

    // Modal state for inventory selection
    const [showInventoryModal, setShowInventoryModal] = useState(false);

    const isLoading = isReservationFetching || isExtrasFetching;
    const isSuccessLoading = isReservationSuccess && isExtrasSuccess;

    const { control, handleSubmit, register, reset, setValue, formState: { errors } } = useForm<CreateBookingExtraFormData>({
        resolver: zodResolver(CreateBookingExtraSchema),
        defaultValues: {
            booking_room_id: reservationId || '',
            item_name: '',
            qty: 1,
            amount: 0,
            date: new Date().toISOString().split('T')[0],
        },
    });

    useEffect(() => {
        if (reservationId) {
            reset({
                booking_room_id: reservationId,
                item_name: '',
                qty: 1,
                amount: 0,
                date: new Date().toISOString().split('T')[0],
            });
        }
    }, [reservationId, reset]);

    // Handle inventory selection from modal
    const handleInventorySelect = (inventory: { id: string; item_name: string; price: string }) => {
        setValue('item_name', inventory.item_name);
        setValue('amount', Number(inventory.price) || 0);
    };

    const { mutate, isPending } = useCreateBookingExtra();
    const { mutate: deleteExtra, isPending: isDeleting } = useDeleteBookingExtra();
    const queryClient = useQueryClient();

    const formatCurrency = (amount: string | number) => {
        const num = typeof amount === 'string' ? parseFloat(amount) : amount;
        return new Intl.NumberFormat('id-ID', {
            style: 'currency',
            currency: 'IDR',
            maximumFractionDigits: 0,
        }).format(num || 0).replace('Rp', 'Rp.').trim();
    };



    const onSubmit = useCallback((values: CreateBookingExtraFormData) => {
        if (!hasRole('dev') && !hasPermission('booking_extra')) {
            toast.error("You don't have permission to do this action");
            return;
        }

        toast.loading('Creating extra...', { id: 'create-extra' });

        mutate({
            ...values,
            booking_room_id: reservationId || '',
        }, {
            onSuccess: async () => {
                await queryClient.refetchQueries();
                toast.success('Extra created successfully', { id: 'create-extra' });
                reset({
                    booking_room_id: reservationId || '',
                    item_name: '',
                    qty: 1,
                    amount: 0,
                    date: new Date().toISOString().split('T')[0],
                });
            },
            onError: () => {
                toast.error('Failed to create extra', { id: 'create-extra' });
            },
        });
    }, [hasRole, hasPermission, mutate, reservationId, queryClient, reset]);

    const handleDelete = useCallback((id: string, source?: string | null) => {
        if (isPosExtra(source)) {
            toast.error(POS_LOCK_MESSAGE);
            return;
        }

        if (!hasRole('dev') && !hasPermission('booking_extra')) {
            toast.error('You do not have permission to delete extra');
            return;
        }

        if (isDeleting) return;
        toast.loading('Removing extra...', { id: 'delete-extra' });
        deleteExtra(id, {
            onSuccess: async () => {
                await queryClient.refetchQueries();
                toast.success('Extra removed successfully', { id: 'delete-extra' });
            },
            onError: () => {
                toast.error('Failed to remove extra', { id: 'delete-extra' });
            },
        });
    }, [deleteExtra, isDeleting, queryClient, hasRole, hasPermission]);

    const buttonIsDisabled = isPending;

    return (
        <div className="container-fluid mt-4">
            {isLoading && (
                <Loader />
            )}
            {!isLoading && isSuccessLoading && (
                <>
                    <div className="row align-items-start mb-3 flex-column flex-md-row">
                        <div className="col mb-3 mb-md-0">
                            <h5 className="mb-0 fs-3 fw-bold text-primary">
                                Add Extra for Booking ID: <strong>#{reservationData.data.invoice_number}</strong>
                            </h5>
                        </div>
                        <div className="col-auto text-start">
                            <div className="mb-2">
                                <strong>Guest Name: </strong>
                                {reservationData.data.customer_name} {reservationData.data.customer_surname || ''}
                            </div>
                            <div>
                                <strong>Room: </strong>
                                {reservationData.data.room?.room_number || 'N/A'} ({reservationData.data.room_type?.name || 'N/A'})
                            </div>
                        </div>
                    </div>

                    <form className="row gy-3 flex-column flex-md-row align-items-end mb-3" onSubmit={handleSubmit(onSubmit)}>
                        <div className="col-12 col-md-auto">
                            <label className="form-label d-flex align-items-center gap-1">
                                Date
                                <InfoTooltip message="The date for this extra charge." />
                            </label>
                            <DateInput
                                className="form-control"
                                defaultValue={new Date().toISOString().split('T')[0]}
                                {...register('date')}
                                disabled={isPending}
                            />
                            {errors.date && (
                                <div className="text-danger mt-1">
                                    {errors.date.message}
                                </div>
                            )}
                        </div>

                        <div className="col-12 col-md-auto">
                            <label className="form-label d-flex align-items-center gap-1">
                                Template
                                <InfoTooltip message="Select a template item (optional)." />
                            </label>
                            <button
                                type="button"
                                className="btn btn-outline-secondary w-100"
                                onClick={() => setShowInventoryModal(true)}
                                disabled={isPending}
                            >
                                <IconClipboardList size={16} className="me-1" />
                                Select Template
                            </button>
                        </div>

                        <div className="col-12 col-md-auto">
                            <label className="form-label d-flex align-items-center gap-1">
                                Item Name
                                <InfoTooltip message="Name of the extra item." />
                            </label>
                            <input
                                type="text"
                                className="form-control"
                                placeholder="e.g. Minibar, Laundry"
                                {...register('item_name')}
                                disabled={isPending}
                            />
                            {errors.item_name && (
                                <div className="text-danger mt-1">
                                    {errors.item_name.message}
                                </div>
                            )}
                        </div>

                        <div className="col-12 col-md-auto">
                            <label className="form-label d-flex align-items-center gap-1">
                                Qty
                                <InfoTooltip message="Quantity of the item." />
                            </label>
                            <Controller
                                name="qty"
                                control={control}
                                render={({ field }) => (
                                    <input
                                        type="number"
                                        className="form-control"
                                        min={1}
                                        {...field}
                                        onChange={(e) => field.onChange(parseInt(e.target.value) || 1)}
                                        disabled={isPending}
                                    />
                                )}
                            />
                            {errors.qty && (
                                <div className="text-danger mt-1">
                                    {errors.qty.message}
                                </div>
                            )}
                        </div>

                        <div className="col-12 col-md-auto">
                            <label className="form-label d-flex align-items-center gap-1">
                                Amount
                                <InfoTooltip message="Total amount for this extra." />
                            </label>
                            <Controller
                                name="amount"
                                control={control}
                                render={({ field, fieldState }) => (
                                    <CurrencyInputRef
                                        name={field.name}
                                        value={Number(field.value) ?? 0}
                                        onChange={(_e, rawValue) => {
                                            field.onChange(parseInt(rawValue) || 0);
                                        }}
                                        error={fieldState.error?.message}
                                        ref={field.ref}
                                    />
                                )}
                            />
                        </div>

                        <div className="col-12 col-md-auto">
                            <button type='submit' className="btn btn-primary w-100 w-md-auto" disabled={buttonIsDisabled}>
                                Add Extra!
                            </button>
                        </div>
                    </form>

                    <div className="table-responsive">
                        <table className="table table-bordered table-striped">
                            <thead className="table-light">
                                <tr>
                                    <th>Date</th>
                                    <th>Item Name</th>
                                    <th>Qty</th>
                                    <th>Amount</th>
                                    <th>Action</th>
                                </tr>
                            </thead>
                            <tbody>
                                {extrasData && extrasData.data.length === 0 && (
                                    <tr>
                                        <td colSpan={5} className="text-center">
                                            No extras found for this booking.
                                        </td>
                                    </tr>
                                )}
                                {extrasData && extrasData.data.length > 0 && extrasData.data.map((extra) => {
                                    const fromPos   = isPosExtra(extra.source);
                                    const cancelled = isCancelledExtra(extra.item_name);

                                    return (
                                        <tr key={extra.id} className={cancelled ? 'text-decoration-line-through text-muted' : undefined}>
                                            <td>{new Date(extra.date).toLocaleDateString()}</td>
                                            <td>
                                                <div className="d-flex align-items-center gap-2">
                                                    <span>{extra.item_name}</span>
                                                    {fromPos && <span className="badge bg-info-subtle text-info-emphasis">POS</span>}
                                                </div>
                                                {/* Rincian item & pajak dari POS. */}
                                                <ExtraDescription description={extra.description} className="mt-1" />
                                            </td>
                                            <td>{extra.qty}</td>
                                            <td>{formatCurrency(extra.amount)}</td>
                                            <td>
                                                {fromPos ? (
                                                    // Terkunci di API — tombolnya sengaja tidak ditawarkan.
                                                    <InfoTooltip message={POS_LOCK_MESSAGE} />
                                                ) : (
                                                    <Button
                                                        variant="danger"
                                                        size="sm"
                                                        onClick={() => handleDelete(extra.id, extra.source)}
                                                        disabled={isDeleting}
                                                    >
                                                        <IconTrash size={16} />
                                                    </Button>
                                                )}
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                </>
            )}

            {/* Inventory Selection Modal */}
            <SelectInventoryModal
                show={showInventoryModal}
                onClose={() => setShowInventoryModal(false)}
                onSelect={handleInventorySelect}
            />
        </div>
    );
};

export default ExtraSection;
